import { View, Dimensions, Platform, Pressable, StyleSheet } from 'react-native';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';
import LottieView from '@utils/lottieAnimation';
import React from 'react';
import { Title, Text } from '@constants/Themed';
import * as Animatable from 'react-native-animatable';
import * as WebBrowser from 'expo-web-browser';
import { H4, Separator, YStack } from 'tamagui';
import { BlurView } from 'expo-blur';
import HorizontalTextCarousel from '@components/Global/Carousel/HorizontalTextCarousel';
import Carousel from 'react-native-reanimated-carousel';
import { useNetInfo } from '@react-native-community/netinfo';
import { isTablet } from 'react-native-device-info';
import { AntDesign } from '@expo/vector-icons';
import globalStyles, { responsivePadding } from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { responsiveFontSize } from '@utils/responsiveFontSize';

type LoginCarouselSlide = {
  title: string;
  descriptor: string;
  type?: 'info' | 'error' | 'warning' | 'success';
};

/** Shown when /api/login-messages is empty or unreachable (common in local dev). */
const DEFAULT_LOGIN_CAROUSEL_SLIDES: LoginCarouselSlide[] = [
  { title: 'dynamicContent1Title', descriptor: 'dynamicContent1Description', type: 'info' },
  { title: 'dynamicContent2Title', descriptor: 'dynamicContent2Description', type: 'info' },
  { title: 'dynamicContent3Title', descriptor: 'dynamicContent3Description', type: 'info' },
  { title: 'dynamicContent4Title', descriptor: 'dynamicContent4Description', type: 'info' },
  { title: 'dynamicContent5Title', descriptor: 'dynamicContent5Description', type: 'info' },
  { title: 'dynamicContent6Title', descriptor: 'dynamicContent6Description', type: 'info' },
  { title: 'dynamicContent7Title', descriptor: 'dynamicContent7Description', type: 'info' },
];

const DynamicContentInfo = ({ modalRef }) => {
  const { t } = useTranslation();
  const loadingRef = useRef<any>(null);
  const width = Dimensions.get('window').width;
  const tablet = isTablet();
  const theme = themeSelector();
  const isLight = Boolean(theme);
  const netInfo = useNetInfo();
  // Simulator often reports isInternetReachable=false while localhost API still works.
  const isOffline =
    netInfo.isConnected === false ||
    (Platform.OS === 'web' && netInfo.isInternetReachable === false);

  const { data, error } = useQuery({
    queryKey: ['login-messages'],
    queryFn: async () => {
      const data = await apis.getLoginMessages()
        .then(res => {
          return res
        }).catch(e => {
          console.log(e);
          return null;
        });
      return data;
    },
    retry: 15,
    refetchInterval: 1000 * 60 * 1, // 5 minutes
    refetchOnWindowFocus: true,
    enabled: !isOffline,
  })

  const carouselSlides =
    Array.isArray(data) && data.length > 0 ? data : DEFAULT_LOGIN_CAROUSEL_SLIDES;

  const surfaceBg = isLight ? 'rgba(255, 255, 255, 0.82)' : 'rgba(15, 23, 42, 0.78)';
  const contrastingBg = isLight ? 'rgba(241, 245, 249, 0.92)' : 'rgba(30, 41, 59, 0.88)';
  const titleColor = isLight ? '#0f172a' : '#f8fafc';
  const bodyColor = isLight ? '#475569' : '#cbd5e1';
  const borderColor = isLight ? 'rgba(148, 163, 184, 0.35)' : 'rgba(148, 163, 184, 0.25)';
  const blurTint = isLight ? 'light' : 'dark';

  useEffect(() => {
    loadingRef.current?.play(0, 200);

    // Cleanup function to clear the interval when the component is unmounted
    return () => {
      if (loadingRef.current) {
        loadingRef.current.reset();
        loadingRef.current = null;
      }
    }
  }, [error]);

  function emailUs() {
    WebBrowser.openBrowserAsync(
      'https://smartsaas.co.uk/privacy-policy/'
    )
  }

  const renderStatusCard = (message: string, showDetail?: boolean) => (
    <Animatable.View
      useNativeDriver
      animation={Platform.OS === 'web' ? 'slideInDown' : 'slideInUp'}
      style={styles.statusOuter}
    >
      <BlurView
        intensity={Platform.OS === 'web' ? 56 : 40}
        tint={blurTint}
        style={[
          styles.statusBlur,
          {
            backgroundColor: contrastingBg,
            borderColor,
          },
        ]}
      >
        <LottieView
          autoPlay
          style={styles.statusLottie}
          source={require('@assets/animations/errorIcon.json')}
          loop={true}
          speed={1}
        />
        <View style={styles.statusTextWrap}>
          <Title
            adjustsFontSizeToFit
            style={{
              fontWeight: '700',
              color: titleColor,
              lineHeight: responsiveFontSize(20),
              fontSize: responsiveFontSize(16),
              marginBottom: showDetail ? responsivePadding(6) : 0,
            }}
          >
            {message}
          </Title>
          {showDetail ? (
            <Pressable onPress={() => emailUs()}>
              <Text
                adjustsFontSizeToFit
                style={{
                  fontSize: responsiveFontSize(12),
                  color: bodyColor,
                  lineHeight: Platform.select({ web: undefined, default: responsiveFontSize(15) }),
                }}
                numberOfLines={4}
              >
                {t('loginOfflineApology', {
                  action: Platform.OS === 'web'
                    ? t('loginOfflineRefreshPage')
                    : t('loginOfflineForceCloseApp'),
                })}
              </Text>
            </Pressable>
          ) : null}
        </View>
      </BlurView>
    </Animatable.View>
  );

  return (
    <View style={[styles.root, { backgroundColor: 'transparent' }]}>
      {isOffline || Boolean(error) ? (
        renderStatusCard(
          t('You are offline, but no worries, you can still login and use SmartSaaS*'),
          Boolean(error),
        )
      ) : (
        <View style={Platform.OS === 'web' ? styles.webCarouselWrap : styles.nativeCarouselWrap}>
          {Platform.OS === 'web' ? (
            <HorizontalTextCarousel
              viewStyle={{ paddingBottom: 0, paddingHorizontal: 0 }}
              height={130}
              content={carouselSlides}
            />
          ) : (
            <Carousel
              loop
              width={width}
              height={tablet ? 108 : 84}
              autoPlay={true}
              data={carouselSlides}
              pagingEnabled
              autoPlayInterval={6000}
              snapEnabled
              enabled={true}
              windowSize={3}
              style={{
                width: '100%',
                flex: 1,
                overflow: 'hidden',
                margin: 0,
                justifyContent: 'flex-end',
                paddingLeft: 0,
                paddingRight: 0,
              }}
              renderItem={({ index, item }: { index: number; item: LoginCarouselSlide }) => {
                const accentBlue = '#3b82f6';
                const accentAmber = '#f59e0b';
                const accentGreen = '#10b981';
                const accentRed = '#ef4444';
                const isAccentSlide = index % 2 === 0;

                const accentColor = item.type === 'success' ? accentGreen
                  : item.type === 'warning' ? accentAmber
                    : item.type === 'error' ? accentRed
                      : isAccentSlide ? accentBlue : accentAmber;

                return (
                  <BlurView
                    key={`carousel-item-${index}-${item.title || 'default'}`}
                    intensity={36}
                    tint={blurTint}
                    style={[
                      globalStyles.modernCard,
                      styles.nativeSlide,
                      {
                        width,
                        height: tablet ? 108 : 84,
                        backgroundColor: surfaceBg,
                        borderColor,
                        borderLeftColor: accentColor,
                      },
                    ]}
                  >
                    <AntDesign
                      onPress={() => modalRef.current?.present()}
                      name={
                        item.type === 'success' ? 'check-circle'
                          : item.type === 'warning' ? 'exclamation-circle'
                            : item.type === 'error' ? 'close-circle'
                              : 'info'
                      }
                      color={accentColor}
                      size={28}
                      style={{ alignSelf: 'center', left: -6 }}
                    />
                    <YStack style={{ flex: 1, justifyContent: 'center' }}>
                      <H4
                        numberOfLines={1}
                        style={{
                          color: titleColor,
                          fontSize: responsiveFontSize(12),
                          fontWeight: '700',
                        }}
                      >
                        {t(item.title)}
                      </H4>
                      <Separator style={{ marginVertical: 4, backgroundColor: borderColor }} />
                      <Text
                        numberOfLines={2}
                        style={{
                          fontSize: responsiveFontSize(11),
                          lineHeight: responsiveFontSize(14),
                          maxWidth: '100%',
                          paddingRight: 12,
                          color: bodyColor,
                        }}
                      >
                        {t(item.descriptor)}
                      </Text>
                    </YStack>
                  </BlurView>
                );
              }}
            />
          )}
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    paddingHorizontal: Platform.select({ web: 0, native: 0 }),
  },
  webCarouselWrap: {
    flex: 1,
    marginHorizontal: -48,
    overflow: 'visible',
  },
  nativeCarouselWrap: {
    flex: 1,
    width: '100%',
    alignSelf: 'stretch',
  },
  statusOuter: {
    flex: 1,
    minHeight: 120,
    maxWidth: 600,
    maxHeight: 180,
  },
  statusBlur: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: responsivePadding(16),
    borderRadius: 20,
    borderWidth: 1,
    overflow: 'hidden',
  },
  statusLottie: {
    alignSelf: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: 5,
    minWidth: 80,
    minHeight: 60,
    maxWidth: 80,
  },
  statusTextWrap: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: responsivePadding(20),
  },
  nativeSlide: {
    justifyContent: 'center',
    paddingVertical: responsivePadding(8),
    paddingHorizontal: responsivePadding(16),
    overflow: 'hidden',
    flex: 1,
    opacity: 1,
    flexDirection: 'row',
    marginVertical: 0,
    borderRadius: 0,
    borderWidth: 1,
    borderLeftWidth: 4,
    shadowOpacity: 0.12,
  },
});

export default DynamicContentInfo;
